import { Bell, Eye, EyeOff, HelpCircle, Lock, Settings, User } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import logoFull from '@/assets/logo-full.png';
import bgImage from '@/assets/papel_parede_sistema.jpg';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/lib/api';

// E-mail e senha pré-preenchidos SÓ em desenvolvimento. Em produção o campo
// vem vazio: o sistema tem mais de um usuário, e o navegador cuida do
// preenchimento via password manager.
const DEFAULT_EMAIL = import.meta.env.DEV ? 'wellington@simplehotel.com.br' : '';
const DEV_PASSWORD = import.meta.env.DEV ? 'Welly@Adm-4583' : '';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(DEFAULT_EMAIL);
  const [password, setPassword] = useState(DEV_PASSWORD);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    if (!email || !password) {
      setError('Preencha e-mail e senha.');
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Credenciais inválidas. Verifique e-mail e senha.');
      } else if (err instanceof ApiError && err.status === 429) {
        setError('Muitas tentativas. Aguarde alguns minutos e tente de novo.');
      } else {
        setError('Erro de conexão com o servidor.');
        console.error(err);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleCancel() {
    setEmail('');
    setPassword('');
    setError(null);
  }

  return (
    <div
      className="flex min-h-screen w-full items-center justify-center p-6"
      style={{
        background:
          'linear-gradient(135deg, hsl(220, 15%, 18%) 0%, hsl(220, 12%, 12%) 100%)',
      }}
    >
      <div className="flex w-full max-w-[960px] rounded-2xl overflow-visible shadow-2xl shadow-black/40 items-center justify-center">
        {/* Painel esquerdo — bege com ícones de ação (desktop) */}
        <aside
          className="hidden lg:flex flex-col items-center justify-center w-[180px] h-[510px] rounded-l-2xl relative"
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="flex flex-col items-center gap-8">
            <button
              type="button"
              data-dica="Atualizações"
              className="w-14 h-14 border-2 border-[hsl(100,15%,55%)] rounded-md flex items-center justify-center bg-[hsl(40,25%,88%)] hover:bg-[hsl(40,25%,82%)] transition-colors"
            >
              <Bell className="w-7 h-7 text-[hsl(100,20%,35%)]" strokeWidth={1.5} />
            </button>
            <a
              href="https://simplehotel.com.br/suporte"
              target="_blank"
              rel="noopener noreferrer"
              data-dica="Suporte"
              className="w-14 h-14 border-2 border-[hsl(100,15%,55%)] rounded-md flex items-center justify-center bg-[hsl(40,25%,88%)] hover:bg-[hsl(40,25%,82%)] transition-colors"
            >
              <HelpCircle className="w-7 h-7 text-[hsl(100,20%,35%)]" strokeWidth={1.5} />
            </a>
            <button
              type="button"
              data-dica="Configurações"
              className="w-14 h-14 border-2 border-[hsl(100,15%,55%)] rounded-md flex items-center justify-center bg-[hsl(40,25%,88%)] hover:bg-[hsl(40,25%,82%)] transition-colors"
            >
              <Settings className="w-7 h-7 text-[hsl(100,20%,35%)]" strokeWidth={1.5} />
            </button>
          </div>
        </aside>

        {/* Painel central — verde com logo e info */}
        <section
          className="flex flex-col items-center justify-between py-10 px-6 w-full max-w-[320px] lg:w-[320px] h-[600px] rounded-2xl z-10 shadow-xl shadow-black/30"
          style={{
            background:
              'linear-gradient(180deg, hsl(90, 30%, 38%) 0%, hsl(100, 28%, 34%) 100%)',
          }}
        >
          <div className="flex flex-col items-center text-center">
            {/* Logo Simple Hotel em alta resolução — sem contornos nem fundo */}
            <img
              src={logoFull}
              alt="Simple Hotel"
              className="h-36 w-auto"
              style={{
                imageRendering: 'auto',
              }}
            />

            {/* Tag diferenciando este módulo do sistema principal */}
            <div className="mt-6 flex items-center gap-2">
              <span className="h-px w-8 bg-white/70" />
              <span className="text-white text-sm tracking-[0.25em] font-bold uppercase">
                Administrativo
              </span>
              <span className="h-px w-8 bg-white/70" />
            </div>

            {/* Bordão Simple Hotel — Dancing Script (Google Fonts) com fallback Lucida Handwriting */}
            <p
              className="text-white mt-7 leading-snug max-w-[270px] text-2xl"
              style={{
                fontFamily:
                  "'Dancing Script', 'Lucida Handwriting', 'Brush Script MT', cursive",
                fontWeight: 600,
                textShadow: '0 1px 2px rgba(0,0,0,0.25)',
              }}
            >
              Pessoas como plataforma de desenvolvimento!
            </p>
          </div>

          <div className="flex flex-col items-center text-center">
            <div className="mb-3 flex items-center justify-center">
              <div className="relative">
                <User className="w-10 h-10 text-white/70" strokeWidth={1.2} />
                <Lock
                  className="w-5 h-5 text-white/70 absolute -bottom-1 -right-1"
                  strokeWidth={1.5}
                />
              </div>
            </div>
            <p className="text-white/80 text-xs leading-relaxed max-w-[200px]">
              Digite e-mail e senha para acessar o sistema
            </p>
          </div>
        </section>

        {/* Painel direito — bege com formulário */}
        <section
          className="flex flex-col items-center justify-center relative h-[510px] w-[300px] rounded-r-2xl"
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <form onSubmit={handleSubmit} className="w-full max-w-[280px] px-4 space-y-8">
            <div>
              <label
                htmlFor="email"
                className="block text-[hsl(100,15%,30%)] font-semibold text-xs tracking-wider mb-2 uppercase"
              >
                E-mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                placeholder="seu.email@empresa.com"
                className="w-full bg-transparent border-0 border-b-2 border-[hsl(100,15%,50%)] text-[hsl(0,0%,25%)] placeholder:text-[hsl(40,10%,60%)] focus:outline-none focus:border-[hsl(100,30%,35%)] py-2 text-sm transition-colors"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-[hsl(100,15%,30%)] font-semibold text-xs tracking-wider mb-2 uppercase"
              >
                Senha
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  placeholder="digite a senha"
                  className="w-full bg-transparent border-0 border-b-2 border-[hsl(100,15%,50%)] text-[hsl(0,0%,25%)] placeholder:text-[hsl(40,10%,60%)] focus:outline-none focus:border-[hsl(100,30%,35%)] py-2 text-sm pr-8 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[hsl(100,15%,45%)] hover:text-[hsl(100,25%,30%)] transition-colors"
                  title={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p role="alert" className="text-sm text-red-600 text-center">
                {error}
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-2.5 rounded-md text-white font-semibold text-sm tracking-wide transition-all disabled:opacity-50"
                style={{ background: 'hsl(90, 30%, 38%)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'hsl(90, 30%, 32%)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'hsl(90, 30%, 38%)')}
              >
                {submitting ? 'Entrando…' : 'Confirmar'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 py-2.5 rounded-md font-semibold text-sm tracking-wide border-2 transition-all"
                style={{
                  borderColor: 'hsl(40, 15%, 65%)',
                  color: 'hsl(0, 0%, 30%)',
                  background: 'hsl(40, 20%, 92%)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'hsl(40, 20%, 88%)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'hsl(40, 20%, 92%)')}
              >
                Cancelar
              </button>
            </div>

            <p className="text-center">
              <Link
                to="/esqueci-senha"
                className="text-xs text-[hsl(100,20%,35%)] underline-offset-2 hover:underline"
              >
                Esqueci minha senha
              </Link>
            </p>
          </form>

          {/* Ícones de ação no mobile (escondidos em desktop) */}
          <div className="flex lg:hidden gap-4 mt-6">
            <button
              type="button"
              data-dica="Configurações"
              className="w-10 h-10 rounded-full flex items-center justify-center bg-white/60 hover:bg-white/80 shadow-md transition-colors"
            >
              <Settings className="w-5 h-5 text-[hsl(100,20%,35%)]" strokeWidth={1.5} />
            </button>
            <a
              href="https://simplehotel.com.br/suporte"
              target="_blank"
              rel="noopener noreferrer"
              data-dica="Suporte"
              className="w-10 h-10 rounded-full flex items-center justify-center bg-white/60 hover:bg-white/80 shadow-md transition-colors"
            >
              <HelpCircle className="w-5 h-5 text-[hsl(100,20%,35%)]" strokeWidth={1.5} />
            </a>
            <button
              type="button"
              data-dica="Atualizações"
              className="w-10 h-10 rounded-full flex items-center justify-center bg-white/60 hover:bg-white/80 shadow-md transition-colors"
            >
              <Bell className="w-5 h-5 text-[hsl(100,20%,35%)]" strokeWidth={1.5} />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
